import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signupProblems, SIGNUP_PASSWORD_MAX, type SignupConfigDto, type SignupInput, type SignupOfferDto, type SignupMode, type SignupStatusDto } from "@delta/shared";
import { useLang, type T } from "../i18n";
import { api, ApiFailure } from "../lib/api";
import { Icon } from "../components/Icon";
import { Wordmark } from "../components/Shell";

// Business signup, linked from the login page. The form keeps everything the owner typed when the server refuses it,
// except the passwords. One request id is generated per attempt and sent again on a retry or a second click, so the
// server creates one business however often the form is sent; a definite answer from the server starts a new attempt.

const FIELD: Record<string, string> = {
  required: "fieldRequired", too_short: "fieldTooShort", too_long: "fieldTooLong", mobile_format: "fieldMobileFormat", email_format: "fieldEmailFormat",
  password_rule: "fieldPasswordRule", password_is_mobile: "fieldPasswordIsMobile", phone_taken: "fieldPhoneTaken", email_taken: "fieldEmailTaken"
};
export const signupFieldText = (t: T, code: string) => t(FIELD[code] ?? "fieldRequired");

/** The login frame (form column and artwork) shared by signup, its status page and recovery. */
export function PublicFrame({ title, lede, wide, children }: { title: string; lede?: ReactNode; wide?: boolean; children: ReactNode }) {
  const { t, lang, setLang } = useLang();
  useEffect(() => { document.title = `${title} · Delta`; }, [title]);
  return (
    <div className="login">
      <section className="login-form-col">
        <div className="login-head"><Link to="/login" aria-label={t("signIn")}><Wordmark light={false} /></Link>
          <button className="btn btn-ghost lang-btn" onClick={() => setLang(lang === "ar" ? "en" : "ar")}><Icon name="globe" />{t("switchLang")}</button>
        </div>
        <div className={`login-body ${wide ? "wide" : ""}`}>
          <h1>{title}</h1>
          {lede && <p className="lede">{lede}</p>}
          {children}
        </div>
        <div className="login-foot"><span>{t("copyright")}</span><a href="https://deltasa.net" target="_blank" rel="noopener">{t("officialSite")} · deltasa.net</a></div>
      </section>
      <section className="login-art" aria-hidden="true">
        <div className="grid-bg" />
        <div><Wordmark light /></div>
        <div><h2>{t("artTitle")}</h2><p>{t("artBody")}</p></div>
        <div />
      </section>
    </div>
  );
}

type Fields = Omit<SignupInput, "requestId" | "language">;
const EMPTY: Fields = { businessName: "", ownerName: "", mobile: "", email: "", password: "" };

export function Signup() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [v, setV] = useState<Fields>(EMPTY);
  const [again, setAgain] = useState("");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<SignupMode | null>(null);
  // A trial offer is shown only when a Delta administrator published one (migration 043); it starts at activation.
  const [offer, setOffer] = useState<SignupOfferDto | null>(null);
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => { api<SignupConfigDto>("GET", "/signup/config").then((c) => { setMode(c.mode); setOffer(c.offer ?? null); }, () => setMode("closed")); }, []);

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) => { setV((x) => ({ ...x, [k]: e.target.value })); setErrs((x) => ({ ...x, [k]: "" })); };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setAlert(null);
    const local: Record<string, string> = Object.fromEntries(Object.entries(signupProblems(v)).map(([k, c]) => [k, signupFieldText(t, c!)]));
    if (!local.password && v.password !== again) local.again = t("signupPasswordMismatch");
    setErrs(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await api<SignupStatusDto>("POST", "/signup", { ...v, requestId: requestId.current, language: lang });
      nav("/signup/status", { replace: true });
    } catch (err) {
      if (err instanceof ApiFailure) {
        // The server answered: nothing was created by this attempt, so the next one is a new attempt.
        if (err.code !== "request_conflict") requestId.current = crypto.randomUUID();
        setErrs(Object.fromEntries(Object.entries(err.fields).map(([k, c]) => [k, signupFieldText(t, c)])));
        setAlert(err.code === "signups_closed" ? t("signupClosed") : err.code === "request_conflict" ? t("signupConflict")
          : err.code === "too_many_attempts" ? t("signupTooMany") : Object.keys(err.fields).length ? t("fixRequired") : t("serverError"));
      } else {
        setAlert(t("signupNetwork"));
      }
      // Secrets are never kept after a failed attempt; everything else stays as typed.
      setV((x) => ({ ...x, password: "" })); setAgain("");
    } finally {
      setBusy(false);
    }
  }

  const input = (k: keyof Fields, label: string, o: { type?: string; ph?: string; auto?: string; full?: boolean; hint?: string; num?: boolean } = {}) => (
    <div className={`field ${o.full ? "full" : ""} ${errs[k] ? "invalid" : ""}`}>
      <label htmlFor={`su-${k}`} className="req">{label}</label>
      <input id={`su-${k}`} name={k} className={`control ${o.num ? "num" : ""}`} type={o.type ?? "text"} autoComplete={o.auto} placeholder={o.ph} value={v[k]} onChange={set(k)}
        maxLength={k === "password" ? SIGNUP_PASSWORD_MAX : 200} aria-invalid={!!errs[k]} style={o.num ? { textAlign: "start" } : undefined} dir={o.num || k === "email" ? "ltr" : undefined} />
      <div className="ferr" role="alert" data-testid={`err-${k}`}>{errs[k]}</div>
      {o.hint && !errs[k] && <span className="hint">{o.hint}</span>}
    </div>
  );

  return (
    <PublicFrame title={t("signupTitle")} lede={t("signupLede")} wide>
      {mode === null ? <p>{t("loading")}</p> : mode === "closed" ? <div className="dlg-alert show" role="alert" data-testid="signup-closed"><Icon name="lock" /><span>{t("signupClosed")}</span></div> : (
        <form id="signup-form" noValidate onSubmit={submit}>
          <div className="su-grid">
            {input("businessName", t("signupBusinessName"), { ph: t("signupBusinessPh"), auto: "organization", full: true })}
            {input("ownerName", t("signupOwnerName"), { ph: t("signupOwnerPh"), auto: "name", full: true })}
            {input("mobile", t("signupMobile"), { type: "tel", ph: t("mobilePh"), auto: "tel", num: true })}
            {input("email", t("signupEmail"), { type: "email", ph: t("signupEmailPh"), auto: "email" })}
            {input("password", t("signupPassword"), { type: "password", auto: "new-password", hint: t("signupPasswordHint") })}
            <div className={`field ${errs.again ? "invalid" : ""}`}>
              <label htmlFor="su-again" className="req">{t("signupPasswordAgain")}</label>
              <input id="su-again" className="control" type="password" autoComplete="new-password" value={again} onChange={(e) => { setAgain(e.target.value); setErrs((x) => ({ ...x, again: "" })); }} maxLength={SIGNUP_PASSWORD_MAX} />
              <div className="ferr" role="alert" data-testid="err-again">{errs.again}</div>
            </div>
          </div>
          {mode === "manual_review" && <div className="su-manual" role="note" data-testid="signup-manual-review"><Icon name="info" /><span><b>{t("signupManualTitle")}</b> {t("signupManualBody")}</span></div>}
          {offer && <div className="su-offer" role="note" data-testid="signup-offer"><Icon name="star" /><span><b>{t("signupOfferTitle", { x: lang === "ar" ? offer.nameAr : offer.nameEn, n: offer.trialDays })}</b> {(lang === "ar" ? offer.descriptionAr : offer.descriptionEn) || ""} {t("signupOfferBody", { n: offer.trialDays })}</span></div>}
          <p className="su-note">{t("signupTerms")}</p>
          {alert && <div className="dlg-alert show" role="alert" data-testid="signup-alert"><Icon name="alert" /><span>{alert}</span></div>}
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? t("signupSubmitting") : t("signupSubmit")}</button>
        </form>
      )}
      <p className="login-alt">{t("signupHaveAccount")} <Link to="/login">{t("signupSignIn")}</Link></p>
    </PublicFrame>
  );
}
